import { useContext, useEffect, useState } from 'react'
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import gaiaTheme from '../../../root-components/theme'
import FinishedProductService from '../../../shared/services/finished-product/finished-product.service'
import { IFinishedProductFull } from '../../../shared/services/finished-product/finished-product.model'
import { ShallowFormDisplay } from '../../presentation-components/form-field-components/general/shallow-form-display'
import moment from 'moment'
import { AddLink, DeleteOutline, DescriptionOutlined, EditOutlined, InfoOutlined, VisibilityOutlined } from '@mui/icons-material'
import PcfReportService from '../../../shared/services/pcf-report/pcf-report.service'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { IProductComponent } from '../../../shared/services/product-component/product-component.model'
import ProductComponentService from '../../../shared/services/product-component/product-component.service'
import { BackButtonLink } from '../../presentation-components/display-elements-components/back-button-link'
import { TooltipHover } from '../../presentation-components/miscellaneous-components/tooltip-hover'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'

export const FinishedProductDetailPage = () => {

  const finishedProductService = new FinishedProductService();
  const productComponentService = new ProductComponentService();
  const pcfReportService = new PcfReportService();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [finishedProduct, setFinishedProduct] = useState<IFinishedProductFull>();
  const [selected, setSelected] = useState<string[]>([]);
  const [isAddingComponent, setIsAddingComponent] = useState<boolean>(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>();
  const [quantityPerProduct, setQuantityPerProduct] = useState<number | undefined>(1);
  const [isCreatingReport, setIsCreatingReport] = useState<boolean>(false);

  // Reuses the most recent draft statement for this product rather than accumulating a new row on
  // every click; a generated one is left alone so its published figures stay put.
  const createFootprintReportHandler = async () => {
    if (!id) return
    setIsCreatingReport(true)
    try {
      const existing = await pcfReportService.getAll(id)
      const draft = existing.find(report => report.status === 'DRAFT')
      const report = draft ?? await pcfReportService.create(id, null)
      navigate(`/products/${id}/footprint-report/${report.id}`)
    } catch (err) {
      console.log(err)
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not open a product footprint report, please try again', backgroundColor: 'text.primary' })
    } finally {
      setIsCreatingReport(false)
    }
  }
  const [productComponents, setProductComponents] = useState<IProductComponent[]>();

  useEffect(() => {
    getProductDetails()
    productComponentService.getAll()
      .then(response => {
        setProductComponents(response)
      })
  }, [])

  const getProductDetails = () => {
    if (id) {
      finishedProductService.getOne(id)
        .then(response => {
          setFinishedProduct(response)
        })
    }
  }

  const componentRows = finishedProduct?.productComponents.map(productComponent => {
    return {
      id: productComponent.id,
      name: productComponent.name,
      quantityPerProduct: productComponent.componentAmount?.quantityPerProduct,
      description: productComponent.description || '',
      createdAt: moment(productComponent.createdAt).format('DD/MM/YYYY'),
      action: (
        <TableActionButtons
          id={productComponent.id!}
          includeView={true}
          includeDelete={true}
          onView={(id: string) => editClickHandler(id)}
          onDelete={(id: string) => deleteClickHandler(id)}
        />
      )
    }
  });

  const editClickHandler = (id: string) => {
    navigate(`/products/component/${id}?back=${finishedProduct!.id}`)
  }

  const deleteClickHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you would like to remove this product component from this product?`,
      buttonCopy: 'Continue',
      hideCancelButton: false
    })
      .then((response) => {
        if (response) {
          finishedProductService.removeProductComponent(finishedProduct!.id!, id)
            .then(response => {
              getProductDetails();
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
            })
        }
      })
  }

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/products/component/${selectedRowId[0]}?back=${finishedProduct!.id}`)
    }
  }

  const handleSelectedProduct = (e: SelectChangeEvent<string>) => {
    setSelectedComponentId(e.target.value)
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true },
    { id: 'description', numeric: false, label: 'Description', sortable: false },
    { id: 'quantityPerProduct', numeric: false, label: 'Quantity per product', sortable: false },
    { id: 'createdAt', numeric: false, label: 'Created on', sortable: true },
  ];

  const cancelAddingProduct = () => {
    setSelectedComponentId(undefined);
    setIsAddingComponent(false);
    setQuantityPerProduct(1);
  }

  const handleAddComponent = () => {
    finishedProductService.addProductComponent(finishedProduct!.id!, selectedComponentId!, quantityPerProduct!)
      .then(response => {
        cancelAddingProduct();
        getProductDetails();
      })
  }

  const handleQuantityPerProduct = (e: any) => {
    if (e.target.value) {
      setQuantityPerProduct(parseFloat(e.target.value))
    } else {
      setQuantityPerProduct(undefined)
    }
  }


  return (
    <>
      <Box sx={{ pb: isMobileMode ? 3 : 6, pl: isMobileMode ? 3 : 6, pr: isMobileMode ? 3 : 6, pt: isMobileMode ? 3 : 5, mt: isMobileMode ? 5 : 0, }}>
        <BackButtonLink buttonText={'Back to products'} link={`/products`} />
        {finishedProduct && <>
          <Typography variant='h4' sx={{ color: 'text.primary', mb: 1, mt: 1 }}> Product Details </Typography>
          <Box sx={{ mb: 8 }}>
            <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mb: 1 }}>
              <Button variant='text' sx={{ mr: 2 }} onClick={createFootprintReportHandler} disabled={isCreatingReport}>
                <DescriptionOutlined sx={{ mr: 0.5 }} />
                <Typography>{isCreatingReport ? 'Preparing…' : 'Product footprint report'}</Typography>
              </Button>
              <Button variant='text' sx={{ mr: 2 }} component={Link} to={`/products/edit/${id}`}>
                <EditOutlined sx={{ mr: 0.5 }} />
                <Typography>Edit</Typography>
              </Button>
            </Stack>
            <ShallowFormDisplay name='Product Name' value={finishedProduct.name} isTopRow={true}></ShallowFormDisplay>
            <ShallowFormDisplay name='Description' value={finishedProduct.description}></ShallowFormDisplay>
            <ShallowFormDisplay name='Configuration' value={finishedProduct.productConfiguration}></ShallowFormDisplay>
            <ShallowFormDisplay name='Stock Keeping Unit' value={finishedProduct.skuCode}></ShallowFormDisplay>
            <ShallowFormDisplay
              name='kg per unit'
              value={finishedProduct.kgCarbonEmission || 'N/A'}
              tooltipElement={<TooltipHover
                title={'Additional emissions'}
                backgroundColor='white'
                text={'The emission figure here will be added to any emission calculated from the listed product components below. You can use this to account for any emissions which are not included in the attatched product components.'}
                placement='bottom'>
                <InfoOutlined sx={{ ml: 1, color: 'text.secondary' }} />
              </TooltipHover>}></ShallowFormDisplay>
            <ShallowFormDisplay name='Created on' value={moment(finishedProduct.createdAt).format('DD/MM/YYYY')} isBottomRow={true}></ShallowFormDisplay>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}> Linked Product Components </Typography>
              {!(isAddingComponent) && productComponents?.some(component => !finishedProduct.productComponents.find(pc => pc.id === component.id)) && (
                <Button variant="text" onClick={() => { setIsAddingComponent(true) }}>
                  <AddLink sx={{ color: 'primary.main', mr: 0.5 }} />
                  <Typography sx={{ color: 'primary.main', display: 'inline' }}>Link Product Component</Typography>
                </Button>
              )}
            </Stack>

            {isAddingComponent && <>
              <Typography variant='body1' sx={{ color: 'text.primary', mb: 1, mt: 2 }}> Please select a component to add: </Typography>
              <FormControl fullWidth sx={{ mb: 2, maxWidth: '400px' }}>
                <InputLabel htmlFor='sector'>Product component</InputLabel>
                <Select
                  value={selectedComponentId || ''}
                  onChange={handleSelectedProduct}
                  fullWidth
                  label="Product component"
                  disabled={productComponents?.filter(component => !finishedProduct.productComponents.find(pc => pc.id === component.id)).length === 0}
                  name="productComponent">
                  {productComponents?.filter(component => !finishedProduct.productComponents.find(pc => pc.id === component.id)).map((component, index) => (
                    <MenuItem key={index}
                      value={component.id}>{component.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2, maxWidth: '400px', display: 'block' }}>
                <InputLabel htmlFor='unitsConsumed'>Quantity per product</InputLabel>
                <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }}
                  label='Quantity per product'
                  name='quantityPepProduct'
                  type='number'
                  fullWidth
                  value={quantityPerProduct || ''}
                  onChange={handleQuantityPerProduct}
                  error={!quantityPerProduct}
                  sx={{ color: 'text.secondary' }} />
              </FormControl>
              <Stack sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
                <Box sx={{ mr: 2 }}>
                  <Button variant='text' onClick={cancelAddingProduct}>
                    <Typography sx={{ color: 'primary.main' }}>Cancel </Typography>
                  </Button>
                </Box>
                <Button onClick={handleAddComponent} disabled={!selectedComponentId}>
                  <Typography>Save</Typography>
                </Button>
              </Stack>
            </>}
          </Box>
          {
            finishedProduct &&
            <TableDisplay
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={componentRows}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected}
              selectByIcon={true} />
          }
        </>}
      </Box>
    </>
  )
}